import { AppDispatch } from '.';
import { messagesActions } from './messages';
import { authenticatedFetch } from '../utils/api';
import { Conversation } from '../models/data';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:3001')
  : '';

export const API_ENDPOINTS = {
  messages: {
    conversations: `${API_BASE_URL}/api/messages/conversations`,
    getConversation: (id: string) => `${API_BASE_URL}/api/messages/conversations/${id}`,
    createConversation: `${API_BASE_URL}/api/messages/conversations`,
    messages: (id: string, before?: string, limit?: number) => {
      const params = new URLSearchParams();
      if (before) params.append('before', before);
      if (limit) params.append('limit', limit.toString());
      const query = params.toString();
      return `${API_BASE_URL}/api/messages/conversations/${id}/messages${query ? `?${query}` : ''}`;
    },
    unreadCount: `${API_BASE_URL}/api/messages/unread-count`,
  },
};

/**
 * Fetch all conversations for the current user
 */
export const fetchConversations = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.messages.conversations);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch conversations');
      }

      const data = await response.json();

      dispatch(messagesActions.setConversations({ conversations: data.conversations }));

      return { success: true, conversations: data.conversations };
    } catch (error: any) {
      console.error('[Messages Actions] Error fetching conversations:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Create a new conversation or get existing one
 */
export const createConversation = (otherUserId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.messages.createConversation, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ otherUserId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create conversation');
      }

      const data = await response.json();

      dispatch(messagesActions.addConversation({ conversation: data.conversation }));

      return { success: true, conversation: data.conversation };
    } catch (error: any) {
      console.error('[Messages Actions] Error creating conversation:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch messages for a conversation with optional pagination
 */
export const fetchMessages = (conversationId: string, before?: string, limit: number = 50) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.messages.messages(conversationId, before, limit)
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch messages');
      }

      const data = await response.json();

      if (before) {
        // Pagination: prepend older messages
        dispatch(messagesActions.prependMessages({
          conversationId,
          messages: data.messages,
        }));
      } else {
        // Initial load: set messages
        dispatch(messagesActions.setMessages({
          conversationId,
          messages: data.messages,
        }));
      }

      return { success: true, messages: data.messages };
    } catch (error: any) {
      console.error('[Messages Actions] Error fetching messages:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Send a message via Socket.io
 * Note: This is handled by socketService, but we export this for consistency
 */
export const sendMessage = (conversationId: string, content: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      // Socket.io will handle the actual sending and optimistic update
      // This is just a placeholder for future REST API fallback if needed
      return { success: true };
    } catch (error: any) {
      console.error('[Messages Actions] Error sending message:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Open a conversation (join room, fetch messages, mark as read)
 */
export const openConversation = (conversation: Conversation) => {
  return async (dispatch: AppDispatch) => {
    try {
      // Set as current conversation
      dispatch(messagesActions.setCurrentConversation({ conversation }));

      // Join conversation via Socket.io (handled by socketService)
      const { socketService } = await import('../services/socketService');
      socketService.joinConversation(conversation.id);

      // Fetch messages if not already loaded
      await dispatch(fetchMessages(conversation.id) as any);

      // Clear unread count for this conversation
      dispatch(messagesActions.clearUnreadCount({ conversationId: conversation.id }));

      // Mark as read if there are messages
      const messagesForConversation = await dispatch(fetchMessages(conversation.id) as any);
      if (messagesForConversation.messages && messagesForConversation.messages.length > 0) {
        const lastMessage = messagesForConversation.messages[messagesForConversation.messages.length - 1];
        socketService.markAsRead(conversation.id, lastMessage.id);
      }

      return { success: true };
    } catch (error: any) {
      console.error('[Messages Actions] Error opening conversation:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Close current conversation (leave room)
 */
export const closeConversation = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { socketService } = await import('../services/socketService');
      const state = (await import('.').then(m => m.default)).getState();
      const currentConversation = state.messages.currentConversation;

      if (currentConversation) {
        socketService.leaveConversation(currentConversation.id);
      }

      dispatch(messagesActions.setCurrentConversation({ conversation: null }));

      return { success: true };
    } catch (error: any) {
      console.error('[Messages Actions] Error closing conversation:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Mark conversation as read
 */
export const markConversationAsRead = (conversationId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const state = (await import('.').then(m => m.default)).getState();
      const messages = state.messages.messages[conversationId];

      if (messages && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        const { socketService } = await import('../services/socketService');
        socketService.markAsRead(conversationId, lastMessage.id);

        dispatch(messagesActions.clearUnreadCount({ conversationId }));
      }

      return { success: true };
    } catch (error: any) {
      console.error('[Messages Actions] Error marking conversation as read:', error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch total unread count
 */
export const fetchUnreadCount = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.messages.unreadCount);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch unread count');
      }

      const data = await response.json();

      dispatch(messagesActions.setUnreadCount({ count: data.unreadCount }));

      return { success: true, unreadCount: data.unreadCount };
    } catch (error: any) {
      console.error('[Messages Actions] Error fetching unread count:', error);
      return { success: false, error: error.message };
    }
  };
};
